import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { speak, playSuccessSound, playErrorSound, playStarSound } from '../utils/speech';

interface Props { onBack: () => void; age: 'little' | 'big'; }

const wordSets = [
  [
    { arabic: 'قطة', emoji: '🐱' }, { arabic: 'كلب', emoji: '🐶' },
    { arabic: 'أسد', emoji: '🦁' }, { arabic: 'طائر', emoji: '🐦' },
    { arabic: 'سمكة', emoji: '🐟' }, { arabic: 'فيل', emoji: '🐘' },
  ],
  [
    { arabic: 'أحمر', emoji: '🔴' }, { arabic: 'أزرق', emoji: '🔵' },
    { arabic: 'أخضر', emoji: '🟢' }, { arabic: 'أصفر', emoji: '🟡' },
    { arabic: 'برتقالي', emoji: '🟠' }, { arabic: 'بنفسجي', emoji: '🟣' },
  ],
];

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export const MatchingGame: React.FC<Props> = ({ onBack, age }) => {
  const setIndex = Math.floor(Math.random() * wordSets.length);
  const count = age === 'little' ? 4 : 6;
  const words = wordSets[setIndex].slice(0, count);

  const [arabicCards, setArabicCards] = useState(() => shuffle(words.map((w, i) => ({ ...w, id: i, matched: false }))));
  const [emojiCards, setEmojiCards] = useState(() => shuffle(words.map((w, i) => ({ ...w, id: i, matched: false }))));
  const [selectedArabic, setSelectedArabic] = useState<number | null>(null);
  const [selectedEmoji, setSelectedEmoji] = useState<number | null>(null);
  const [wrong, setWrong] = useState<[number, number] | null>(null);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);
  const [stars, setStars] = useState(0);

  const totalMatches = words.length;

  useEffect(() => {
    if (score === totalMatches) {
      setTimeout(() => {
        playStarSound();
        setStars(score === totalMatches ? 3 : score >= totalMatches * 0.7 ? 2 : 1);
        setDone(true);
      }, 500);
    }
  }, [score, totalMatches]);

  const handleArabic = useCallback((id: number, arabic: string) => {
    if (arabicCards.find(c => c.id === id)?.matched) return;
    speak(arabic, 'ar');
    setSelectedArabic(id);
  }, [arabicCards]);

  const handleEmoji = useCallback((id: number) => {
    if (emojiCards.find(c => c.id === id)?.matched) return;
    setSelectedEmoji(id);
  }, [emojiCards]);

  useEffect(() => {
    if (selectedArabic !== null && selectedEmoji !== null) {
      if (selectedArabic === selectedEmoji) {
        // Match!
        playSuccessSound();
        setArabicCards(prev => prev.map(c => c.id === selectedArabic ? { ...c, matched: true } : c));
        setEmojiCards(prev => prev.map(c => c.id === selectedEmoji ? { ...c, matched: true } : c));
        setScore(s => s + 1);
        setSelectedArabic(null);
        setSelectedEmoji(null);
      } else {
        // No match
        playErrorSound();
        setWrong([selectedArabic, selectedEmoji]);
        setTimeout(() => {
          setSelectedArabic(null);
          setSelectedEmoji(null);
          setWrong(null);
        }, 800);
      }
    }
  }, [selectedArabic, selectedEmoji]);

  if (done) return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '20px' }}>
      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring' }}>
        <div style={{ fontSize: '80px', marginBottom: '20px' }}>
          {'⭐'.repeat(stars)}
        </div>
        <h2 style={{ fontFamily: "'Fredoka One', cursive", fontSize: '3rem', color: '#FFE66D', textShadow: '2px 2px 0 #FF9F43', marginBottom: '10px' }}>
          {stars === 3 ? 'مبروووك! 🎉' : 'أحسنت! 🌟'}
        </h2>
        <p style={{ fontSize: '1.3rem', color: '#636E72', marginBottom: '30px' }}>You matched all the words!</p>
        <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <motion.button whileTap={{ scale: 0.9 }} onClick={() => window.location.reload()}
            style={{ background: '#FF6B6B', border: 'none', borderRadius: '50px', padding: '15px 30px', color: 'white', fontFamily: "'Fredoka One', cursive", fontSize: '1.2rem', cursor: 'pointer' }}>
            Play Again 🔄
          </motion.button>
          <motion.button whileTap={{ scale: 0.9 }} onClick={onBack}
            style={{ background: 'white', border: '3px solid #FF6B6B', borderRadius: '50px', padding: '15px 30px', color: '#FF6B6B', fontFamily: "'Fredoka One', cursive", fontSize: '1.2rem', cursor: 'pointer' }}>
            Menu 🏠
          </motion.button>
        </div>
      </motion.div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', padding: '20px' }}>
      <div style={{ maxWidth: '700px', margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <motion.button whileTap={{ scale: 0.9 }} onClick={onBack}
            style={{ background: 'white', border: '3px solid #FFE66D', borderRadius: '50px', padding: '10px 20px', cursor: 'pointer', color: '#FF9F43', fontWeight: 700 }}>
            ← Back
          </motion.button>
          <h2 style={{ fontFamily: "'Fredoka One', cursive", color: '#FF9F43', fontSize: '1.8rem' }}>
            Match it! 🎮
          </h2>
          <div style={{ fontFamily: "'Fredoka One', cursive", fontSize: '1.3rem', color: '#FF9F43' }}>
            {score}/{totalMatches} ⭐
          </div>
        </div>
        <p style={{ textAlign: 'center', color: '#636E72', marginBottom: '25px', fontSize: '1rem' }}>
          Tap a word, then tap its matching picture! 👆
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          {/* Arabic words */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <p style={{ textAlign: 'center', fontWeight: 700, color: '#636E72', marginBottom: '5px' }}>Words كلمات</p>
            {arabicCards.map(card => {
              const isSelected = selectedArabic === card.id;
              const isWrong = wrong?.[0] === card.id;
              return (
                <motion.button key={card.id} whileHover={!card.matched ? { scale: 1.05 } : {}}
                  whileTap={!card.matched ? { scale: 0.95 } : {}}
                  onClick={() => !card.matched && handleArabic(card.id, card.arabic)}
                  animate={isWrong ? { x: [-5, 5, -5, 5, 0] } : {}}
                  style={{
                    background: card.matched ? '#00B894' : isSelected ? '#6C63FF' : isWrong ? '#FF7675' : 'white',
                    border: `3px solid ${card.matched ? '#00B894' : isSelected ? '#6C63FF' : isWrong ? '#FF7675' : '#E0E0E0'}`,
                    borderRadius: '16px', padding: '18px', fontSize: '1.5rem',
                    direction: 'rtl', cursor: card.matched ? 'default' : 'pointer',
                    color: card.matched || isSelected || isWrong ? 'white' : '#2D3436',
                    fontWeight: 700, transition: 'all 0.2s', opacity: card.matched ? 0.6 : 1
                  }}>
                  {card.matched ? '✓ ' : ''}{card.arabic}
                </motion.button>
              );
            })}
          </div>

          {/* Emoji pictures */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <p style={{ textAlign: 'center', fontWeight: 700, color: '#636E72', marginBottom: '5px' }}>Pictures صور</p>
            {emojiCards.map(card => {
              const isSelected = selectedEmoji === card.id;
              const isWrong = wrong?.[1] === card.id;
              return (
                <motion.button key={card.id} whileHover={!card.matched ? { scale: 1.05 } : {}}
                  whileTap={!card.matched ? { scale: 0.95 } : {}}
                  onClick={() => !card.matched && handleEmoji(card.id)}
                  animate={isWrong ? { x: [-5, 5, -5, 5, 0] } : {}}
                  style={{
                    background: card.matched ? '#00B894' : isSelected ? '#FF6B6B' : isWrong ? '#FF7675' : 'white',
                    border: `3px solid ${card.matched ? '#00B894' : isSelected ? '#FF6B6B' : isWrong ? '#FF7675' : '#E0E0E0'}`,
                    borderRadius: '16px', padding: '15px', fontSize: '2.5rem',
                    cursor: card.matched ? 'default' : 'pointer', transition: 'all 0.2s', opacity: card.matched ? 0.6 : 1
                  }}>
                  {card.emoji}
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
